import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Search, ChevronLeft, ChevronRight, Building2, Plus, Pencil, Trash2 } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { PageHeaderActionsRight, PageHeaderTitleSuffix } from "@/contexts/PageHeaderContext";
import { formatApiErrorDetail } from "@/lib/api";
import { can, ROLE_LABELS } from "@/lib/rbac";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COMPANY_LIST = { type: "company" };

const COMPANY_STEPS = [
  { id: 1, label: "Basic details" },
  { id: 2, label: "Billing details" },
  { id: 3, label: "Company admin" },
];

const emptyBilling = {
  gst_number: "",
  pan_number: "",
  billing_name: "",
  billing_address: "",
  billing_email: "",
  billing_phone: "",
  bank_name: "",
  bank_account_name: "",
  bank_account_number: "",
  bank_ifsc: "",
};

const BYTES_PER_GB = 1024 ** 3;

const bytesToGbInput = (bytes) => {
  if (bytes == null || bytes === "") return "";
  const n = Number(bytes);
  if (!Number.isFinite(n) || n < 0) return "";
  const gb = n / BYTES_PER_GB;
  return String(Math.round(gb * 100) / 100);
};

const gbInputToBytes = (gb) => {
  const trimmed = String(gb ?? "").trim();
  if (!trimmed) return null;
  const n = Number(trimmed);
  if (!Number.isFinite(n) || n < 0) return NaN;
  return Math.round(n * BYTES_PER_GB);
};

const emptyForm = {
  name: "",
  address: "",
  contact_email: "",
  contact_phone: "",
  is_active: true,
  storage_quota_gb: "20",
  billing: { ...emptyBilling },
};

const emptyAdminForm = {
  name: "",
  email: "",
  password: "",
};

const formatDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

const buildBillingPayload = (billing = {}) => ({
  gst_number: billing.gst_number?.trim() || null,
  pan_number: billing.pan_number?.trim() || null,
  billing_name: billing.billing_name?.trim() || null,
  billing_address: billing.billing_address?.trim() || null,
  billing_email: billing.billing_email?.trim() || null,
  billing_phone: billing.billing_phone?.trim() || null,
  bank_name: billing.bank_name?.trim() || null,
  bank_account_name: billing.bank_account_name?.trim() || null,
  bank_account_number: billing.bank_account_number?.trim() || null,
  bank_ifsc: billing.bank_ifsc?.trim() || null,
});

const Organizations = () => {
  const navigate = useNavigate();
  const { permissions } = useAuth();
  const canManage = can(permissions, "manage_reference_data");

  const [organizations, setOrganizations] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingOrg, setEditingOrg] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [adminForm, setAdminForm] = useState(emptyAdminForm);
  const [step, setStep] = useState(1);
  const [createdCompany, setCreatedCompany] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const fetchOrganizations = useCallback(async (currentPage = page, currentSearch = search) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/organizations`, {
        params: {
          type: COMPANY_LIST.type,
          page: currentPage,
          limit,
          search: currentSearch || undefined,
        },
      });
      const items = res.data?.organizations ?? res.data?.items ?? [];
      const typed = items.filter((org) => String(org?.type ?? "").toLowerCase() === "company");
      const backendFiltered = items.length > 0 && typed.length === items.length;
      const rows = backendFiltered ? items : typed;
      setOrganizations(rows);
      setTotal(backendFiltered ? (res.data?.total ?? rows.length) : rows.length);
    } catch (e) {
      console.error(e);
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load companies");
      setOrganizations([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [limit, page, search]);

  useEffect(() => {
    fetchOrganizations(page, search);
  }, [fetchOrganizations, page, search]);

  const resetDialogState = () => {
    setEditingOrg(null);
    setForm({ ...emptyForm, billing: { ...emptyBilling } });
    setAdminForm(emptyAdminForm);
    setStep(1);
    setCreatedCompany(null);
  };

  const handleDialogOpenChange = (open) => {
    setDialogOpen(open);
    if (!open) resetDialogState();
  };

  const handleSearchSubmit = (e) => {
    e?.preventDefault?.();
    setPage(1);
    setSearch(searchInput.trim());
  };

  const handleRowClick = (org) => {
    if (!org?.id) return;
    navigate(`/organizations/${org.id}`);
  };

  const openCreate = () => {
    resetDialogState();
    setDialogOpen(true);
  };

  const openEdit = (org, e) => {
    e?.stopPropagation?.();
    if (!org?.id) return;
    setEditingOrg(org);
    setStep(1);
    setCreatedCompany(null);
    setAdminForm(emptyAdminForm);
    setForm({
      name: org.name ?? "",
      address: org.address ?? "",
      contact_email: org.contact_email ?? "",
      contact_phone: org.contact_phone ?? "",
      is_active: org.is_active !== false,
      storage_quota_gb: bytesToGbInput(org.storage_quota_bytes),
      billing: {
        ...emptyBilling,
        ...(org.billing ?? {}),
      },
    });
    setDialogOpen(true);
  };

  const updateBillingField = (key, value) => {
    setForm((prev) => ({
      ...prev,
      billing: { ...prev.billing, [key]: value },
    }));
  };

  const buildOrgPayload = ({ includeBilling = false } = {}) => {
    const storageQuotaBytes = gbInputToBytes(form.storage_quota_gb);
    const payload = {
      name: form.name.trim(),
      type: "company",
      address: form.address.trim() || null,
      contact_email: form.contact_email.trim() || null,
      contact_phone: form.contact_phone.trim() || null,
      comes_under: null,
      is_active: form.is_active !== false,
      storage_quota_bytes: storageQuotaBytes,
    };
    if (includeBilling) {
      payload.billing = buildBillingPayload(form.billing);
    }
    return payload;
  };

  const quotaIsInvalid = () => Number.isNaN(gbInputToBytes(form.storage_quota_gb));

  const handleSaveSimple = async () => {
    if (!form.name?.trim()) {
      toast.error("Name is required");
      return;
    }
    if (quotaIsInvalid()) {
      toast.error("Storage quota must be a non-negative number of GB");
      return;
    }
    setSaving(true);
    try {
      const payload = buildOrgPayload({ includeBilling: false });
      if (editingOrg?.id) {
        await axios.put(`${API}/organizations/${editingOrg.id}`, payload);
        toast.success("Company updated");
      } else {
        await axios.post(`${API}/organizations`, payload);
        toast.success("Company created");
      }
      handleDialogOpenChange(false);
      await fetchOrganizations(page, search);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to save company");
    } finally {
      setSaving(false);
    }
  };

  const handleCompanyStep1Next = () => {
    if (!form.name?.trim()) {
      toast.error("Name is required");
      return;
    }
    if (quotaIsInvalid()) {
      toast.error("Storage quota must be a non-negative number of GB");
      return;
    }
    setStep(2);
  };

  const handleCompanyStep2Next = async () => {
    if (createdCompany?.id) {
      setStep(3);
      return;
    }
    if (!form.name?.trim()) {
      toast.error("Name is required");
      return;
    }
    if (quotaIsInvalid()) {
      toast.error("Storage quota must be a non-negative number of GB");
      return;
    }
    setSaving(true);
    try {
      const payload = buildOrgPayload({ includeBilling: true });
      const res = await axios.post(`${API}/organizations`, payload);
      const company = res.data;
      if (!company?.id) throw new Error("Company created without id");
      setCreatedCompany(company);
      toast.success("Company created");
      await fetchOrganizations(page, search);
      setStep(3);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to save company");
    } finally {
      setSaving(false);
    }
  };

  const handleCreateCompanyAdmin = async () => {
    if (!createdCompany?.id) {
      toast.error("Company was not created. Go back and try again.");
      return;
    }
    if (!adminForm.name?.trim() || !adminForm.email?.trim()) {
      toast.error("Name and email are required");
      return;
    }
    if (!adminForm.password || adminForm.password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/users`, {
        name: adminForm.name.trim(),
        email: adminForm.email.trim(),
        password: adminForm.password,
        role: "company_admin",
        organization_id: createdCompany.id,
      });
      toast.success("Company admin created. Share the password securely.");
      handleDialogOpenChange(false);
      await fetchOrganizations(page, search);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to create company admin");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget?.id) return;
    setSaving(true);
    try {
      await axios.delete(`${API}/organizations/${deleteTarget.id}`);
      toast.success("Company deleted");
      setDeleteTarget(null);
      await fetchOrganizations(page, search);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to delete company");
    } finally {
      setSaving(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / limit));
  const singularLabel = "Company";
  const isCompanyCreate = !editingOrg;
  const dialogWide = isCompanyCreate;

  return (
    <div className="flex flex-col flex-1 min-h-0 gap-3" data-testid="organizations-page">
      <PageHeaderTitleSuffix>
        {!loading ? `(${total})` : null}
      </PageHeaderTitleSuffix>

      <PageHeaderActionsRight>
        <form
          onSubmit={handleSearchSubmit}
          className="flex items-center gap-2 sm:gap-3 flex-wrap justify-end"
        >
          <div className="relative min-w-0 w-[min(100%,16rem)] sm:w-56 lg:w-72">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--xl-mut2)] pointer-events-none" />
            <Input
              data-testid="organization-search"
              placeholder="Search companies..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="h-10 pl-10 text-sm border-[var(--xl-line2)] bg-white"
              aria-label="Search companies"
            />
          </div>
          <Button type="submit" variant="outline" className="h-10 border-[var(--xl-line2)] bg-white">
            Search
          </Button>
          {canManage ? (
            <Button
              type="button"
              onClick={openCreate}
              className="h-10 gap-1.5"
              data-testid="add-organization-btn"
            >
              <Plus className="w-4 h-4" />
              <span className="hidden sm:inline">Add {singularLabel}</span>
              <span className="sm:hidden">Add</span>
            </Button>
          ) : null}
        </form>
      </PageHeaderActionsRight>

      <Card className="overflow-hidden flex-1 min-h-0 flex flex-col rounded-2xl border border-[var(--xl-line)] bg-white shadow-[0_1px_3px_rgba(0,0,0,0.04)]">
        <div className="overflow-auto flex-1 min-h-0">
          <Table className="sticky-table min-w-[900px]">
            <TableHeader>
              <TableRow className="hover:bg-[var(--xl-black)] border-0">
                <TableHead className="min-w-[220px]">{singularLabel}</TableHead>
                <TableHead className="min-w-[180px]">Contact</TableHead>
                <TableHead className="min-w-[200px]">Address</TableHead>
                <TableHead className="w-24 text-right">Logins</TableHead>
                <TableHead className="w-24 text-right">Cases</TableHead>
                <TableHead className="w-28">Status</TableHead>
                <TableHead className="w-36">Created</TableHead>
                {canManage ? <TableHead className="w-28 text-right">Actions</TableHead> : null}
              </TableRow>
            </TableHeader>
            <TableBody className="text-sm font-normal text-[var(--xl-text)]">
              {loading ? (
                <TableRow>
                  <TableCell colSpan={canManage ? 8 : 7} className="text-center py-8 text-muted-foreground font-normal">
                    Loading...
                  </TableCell>
                </TableRow>
              ) : organizations.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={canManage ? 8 : 7} className="text-center py-8 text-muted-foreground font-normal">
                    No companies found
                  </TableCell>
                </TableRow>
              ) : (
                organizations.map((org, i) => (
                  <TableRow
                    key={org.id ?? org.name ?? i}
                    data-testid={`organization-row-${i}`}
                    className="cursor-pointer hover:bg-black/[0.02]"
                    onClick={() => handleRowClick(org)}
                  >
                    <TableCell className="min-w-[220px]">
                      <div className="flex items-center gap-2.5 min-w-0">
                        <div
                          className="w-8 h-8 rounded-full flex-shrink-0 grid place-items-center bg-[var(--xl-primary)]/10 text-[var(--xl-primary)]"
                          aria-hidden
                        >
                          <Building2 className="w-4 h-4" />
                        </div>
                        <div className="min-w-0">
                          <div className="font-semibold text-[var(--xl-text)] truncate">
                            {org.name || "—"}
                          </div>
                          {org.type ? (
                            <div className="text-xs text-[var(--xl-mut)] mt-0.5 capitalize truncate">
                              {org.type}
                            </div>
                          ) : null}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="min-w-[180px]">
                      <div className="min-w-0">
                        <div className="truncate">{org.contact_email || "—"}</div>
                        {org.contact_phone ? (
                          <div className="text-xs text-[var(--xl-mut)] mt-0.5 truncate">
                            {org.contact_phone}
                          </div>
                        ) : null}
                      </div>
                    </TableCell>
                    <TableCell className="min-w-[200px]">
                      <span className="line-clamp-2">{org.address || "—"}</span>
                    </TableCell>
                    <TableCell className="w-24 text-right tabular-nums">
                      {org.active_logins ?? 0}
                    </TableCell>
                    <TableCell className="w-24 text-right tabular-nums">
                      {org.cases ?? 0}
                    </TableCell>
                    <TableCell className="w-28">
                      <Badge
                        variant="outline"
                        className={
                          org.is_active !== false
                            ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                            : "bg-slate-100 text-slate-600 border-slate-200"
                        }
                      >
                        {org.is_active !== false ? "Active" : "Inactive"}
                      </Badge>
                    </TableCell>
                    <TableCell className="w-36 text-[var(--xl-mut)]">
                      {formatDate(org.created_at)}
                    </TableCell>
                    {canManage ? (
                      <TableCell className="w-28">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            aria-label={`Edit ${org.name || singularLabel}`}
                            data-testid={`edit-organization-${i}`}
                            onClick={(e) => openEdit(org, e)}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-destructive hover:text-destructive"
                            aria-label={`Delete ${org.name || singularLabel}`}
                            data-testid={`delete-organization-${i}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              setDeleteTarget(org);
                            }}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    ) : null}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {total > limit && (
          <div className="flex items-center justify-between gap-3 px-4 py-3 border-t border-[var(--xl-line)]">
            <p className="text-xs text-[var(--xl-mut)]">
              Page {page} of {totalPages} · {total} companies
            </p>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={page <= 1 || loading}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="h-8 gap-1"
                data-testid="organizations-prev-page"
              >
                <ChevronLeft className="w-4 h-4" /> Prev
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={page >= totalPages || loading}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                className="h-8 gap-1"
                data-testid="organizations-next-page"
              >
                Next <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </Card>

      <Dialog open={dialogOpen} onOpenChange={handleDialogOpenChange}>
        <DialogContent
          className={
            dialogWide
              ? "w-[min(100%,40rem)] max-h-[90vh] overflow-y-auto"
              : "w-[min(100%,28rem)]"
          }
        >
          <DialogHeader>
            <DialogTitle>
              {editingOrg ? `Edit ${singularLabel}` : `Create ${singularLabel}`}
            </DialogTitle>
            <DialogDescription>
              {isCompanyCreate
                ? "Add company details, billing, then create the company admin."
                : editingOrg
                  ? `Update ${singularLabel.toLowerCase()} details.`
                  : `Add a new ${singularLabel.toLowerCase()} to your network.`}
            </DialogDescription>
          </DialogHeader>

          {isCompanyCreate ? (
            <div className="flex items-center gap-2 mb-1" aria-label="Form steps">
              {COMPANY_STEPS.map((s, idx, arr) => (
                <div key={s.id} className="flex items-center gap-2 min-w-0 flex-1">
                  <div
                    className={`flex items-center gap-2 min-w-0 ${
                      step === s.id
                        ? "text-[var(--xl-primary)]"
                        : step > s.id
                          ? "text-emerald-700"
                          : "text-[var(--xl-mut)]"
                    }`}
                  >
                    <span
                      className={`w-6 h-6 rounded-full text-xs font-semibold grid place-items-center flex-shrink-0 ${
                        step === s.id
                          ? "bg-[var(--xl-primary)] text-white"
                          : step > s.id
                            ? "bg-emerald-100 text-emerald-700"
                            : "bg-[var(--xl-bg)] border border-[var(--xl-line)]"
                      }`}
                    >
                      {s.id}
                    </span>
                    <span className="text-xs font-medium truncate hidden sm:inline">{s.label}</span>
                  </div>
                  {idx < arr.length - 1 ? (
                    <div className="h-px flex-1 bg-[var(--xl-line)] min-w-2" aria-hidden />
                  ) : null}
                </div>
              ))}
            </div>
          ) : null}

          {!isCompanyCreate || step === 1 ? (
            <div className="grid gap-3 py-1">
              <div className="grid gap-1.5">
                <Label htmlFor="org-name">Name *</Label>
                <Input
                  id="org-name"
                  data-testid="org-input-name"
                  value={form.name}
                  onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                  placeholder={`${singularLabel} name`}
                  disabled={Boolean(createdCompany?.id)}
                />
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor="org-email">Contact email</Label>
                <Input
                  id="org-email"
                  type="email"
                  data-testid="org-input-email"
                  value={form.contact_email}
                  onChange={(e) => setForm((prev) => ({ ...prev, contact_email: e.target.value }))}
                  placeholder="contact@example.com"
                  disabled={Boolean(createdCompany?.id)}
                />
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor="org-phone">Contact phone</Label>
                <Input
                  id="org-phone"
                  data-testid="org-input-phone"
                  value={form.contact_phone}
                  onChange={(e) => setForm((prev) => ({ ...prev, contact_phone: e.target.value }))}
                  placeholder="+1-555-0123"
                  disabled={Boolean(createdCompany?.id)}
                />
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor="org-address">Address</Label>
                <Input
                  id="org-address"
                  data-testid="org-input-address"
                  value={form.address}
                  onChange={(e) => setForm((prev) => ({ ...prev, address: e.target.value }))}
                  placeholder="Street, city"
                  disabled={Boolean(createdCompany?.id)}
                />
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor="org-storage-quota">Storage quota (GB)</Label>
                <Input
                  id="org-storage-quota"
                  type="number"
                  min="0"
                  step="0.01"
                  data-testid="org-input-storage-quota"
                  value={form.storage_quota_gb}
                  onChange={(e) => setForm((prev) => ({ ...prev, storage_quota_gb: e.target.value }))}
                  placeholder="20"
                  disabled={Boolean(createdCompany?.id)}
                  aria-label="Storage quota in gigabytes"
                />
              </div>
              <div className="flex items-center justify-between gap-3 rounded-lg border border-[var(--xl-line)] px-3 py-2.5">
                <div className="min-w-0">
                  <Label htmlFor="org-active">Active</Label>
                  <p className="text-xs text-[var(--xl-mut)] mt-0.5">
                    Inactive companies stay in the list but are marked inactive
                  </p>
                </div>
                <Switch
                  id="org-active"
                  checked={form.is_active !== false}
                  onCheckedChange={(checked) => setForm((prev) => ({ ...prev, is_active: Boolean(checked) }))}
                  disabled={Boolean(createdCompany?.id)}
                  data-testid="org-input-active"
                  aria-label="Company active"
                />
              </div>
            </div>
          ) : null}

          {isCompanyCreate && step === 2 ? (
            <div className="grid gap-3 py-1">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="grid gap-1.5">
                  <Label htmlFor="billing-name">Billing name</Label>
                  <Input
                    id="billing-name"
                    data-testid="org-billing-name"
                    value={form.billing.billing_name}
                    onChange={(e) => updateBillingField("billing_name", e.target.value)}
                    placeholder="Legal billing name"
                    disabled={Boolean(createdCompany?.id)}
                  />
                </div>
                <div className="grid gap-1.5">
                  <Label htmlFor="gst-number">GST number</Label>
                  <Input
                    id="gst-number"
                    data-testid="org-gst-number"
                    value={form.billing.gst_number}
                    onChange={(e) => updateBillingField("gst_number", e.target.value)}
                    placeholder="33AABCA1234C1Z5"
                    disabled={Boolean(createdCompany?.id)}
                  />
                </div>
                <div className="grid gap-1.5">
                  <Label htmlFor="pan-number">PAN number</Label>
                  <Input
                    id="pan-number"
                    data-testid="org-pan-number"
                    value={form.billing.pan_number}
                    onChange={(e) => updateBillingField("pan_number", e.target.value)}
                    placeholder="AABCA1234C"
                    disabled={Boolean(createdCompany?.id)}
                  />
                </div>
                <div className="grid gap-1.5">
                  <Label htmlFor="billing-email">Billing email</Label>
                  <Input
                    id="billing-email"
                    type="email"
                    data-testid="org-billing-email"
                    value={form.billing.billing_email}
                    onChange={(e) => updateBillingField("billing_email", e.target.value)}
                    placeholder="billing@example.com"
                    disabled={Boolean(createdCompany?.id)}
                  />
                </div>
                <div className="grid gap-1.5">
                  <Label htmlFor="billing-phone">Billing phone</Label>
                  <Input
                    id="billing-phone"
                    data-testid="org-billing-phone"
                    value={form.billing.billing_phone}
                    onChange={(e) => updateBillingField("billing_phone", e.target.value)}
                    placeholder="+91-44-28290201"
                    disabled={Boolean(createdCompany?.id)}
                  />
                </div>
                <div className="grid gap-1.5 sm:col-span-2">
                  <Label htmlFor="billing-address">Billing address</Label>
                  <Input
                    id="billing-address"
                    data-testid="org-billing-address"
                    value={form.billing.billing_address}
                    onChange={(e) => updateBillingField("billing_address", e.target.value)}
                    placeholder="Street, city, pincode"
                    disabled={Boolean(createdCompany?.id)}
                  />
                </div>
                <div className="grid gap-1.5">
                  <Label htmlFor="bank-name">Bank name</Label>
                  <Input
                    id="bank-name"
                    data-testid="org-bank-name"
                    value={form.billing.bank_name}
                    onChange={(e) => updateBillingField("bank_name", e.target.value)}
                    placeholder="HDFC Bank"
                    disabled={Boolean(createdCompany?.id)}
                  />
                </div>
                <div className="grid gap-1.5">
                  <Label htmlFor="bank-account-name">Account name</Label>
                  <Input
                    id="bank-account-name"
                    data-testid="org-bank-account-name"
                    value={form.billing.bank_account_name}
                    onChange={(e) => updateBillingField("bank_account_name", e.target.value)}
                    placeholder="Account holder name"
                    disabled={Boolean(createdCompany?.id)}
                  />
                </div>
                <div className="grid gap-1.5">
                  <Label htmlFor="bank-account-number">Account number</Label>
                  <Input
                    id="bank-account-number"
                    data-testid="org-bank-account-number"
                    value={form.billing.bank_account_number}
                    onChange={(e) => updateBillingField("bank_account_number", e.target.value)}
                    placeholder="50200012345678"
                    disabled={Boolean(createdCompany?.id)}
                  />
                </div>
                <div className="grid gap-1.5">
                  <Label htmlFor="bank-ifsc">IFSC</Label>
                  <Input
                    id="bank-ifsc"
                    data-testid="org-bank-ifsc"
                    value={form.billing.bank_ifsc}
                    onChange={(e) => updateBillingField("bank_ifsc", e.target.value)}
                    placeholder="HDFC0001234"
                    disabled={Boolean(createdCompany?.id)}
                  />
                </div>
              </div>
            </div>
          ) : null}

          {isCompanyCreate && step === 3 ? (
            <div className="grid gap-3 py-1">
              <div className="rounded-lg border border-[var(--xl-line)] bg-[var(--xl-bg)] px-3 py-2.5 text-sm">
                <p className="text-xs text-[var(--xl-mut)] mb-1">Creating admin for</p>
                <p className="font-medium truncate">{createdCompany?.name || form.name}</p>
                <p className="text-xs text-[var(--xl-mut)] mt-1">
                  Role: {ROLE_LABELS.company_admin ?? "Company Admin"} (default)
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="grid gap-1.5">
                  <Label htmlFor="admin-name">Full name *</Label>
                  <Input
                    id="admin-name"
                    data-testid="org-admin-name"
                    value={adminForm.name}
                    onChange={(e) => setAdminForm((prev) => ({ ...prev, name: e.target.value }))}
                  />
                </div>
                <div className="grid gap-1.5">
                  <Label htmlFor="admin-email">Email *</Label>
                  <Input
                    id="admin-email"
                    type="email"
                    data-testid="org-admin-email"
                    value={adminForm.email}
                    onChange={(e) => setAdminForm((prev) => ({ ...prev, email: e.target.value }))}
                  />
                </div>
                <div className="grid gap-1.5 sm:col-span-2">
                  <Label htmlFor="admin-password">Initial password * (min 8 chars)</Label>
                  <Input
                    id="admin-password"
                    type="text"
                    data-testid="org-admin-password"
                    value={adminForm.password}
                    onChange={(e) => setAdminForm((prev) => ({ ...prev, password: e.target.value }))}
                    placeholder="e.g. XLSync@2026"
                  />
                </div>
              </div>
            </div>
          ) : null}

          <DialogFooter className="gap-2">
            {!isCompanyCreate ? (
              <>
                <Button type="button" variant="outline" onClick={() => handleDialogOpenChange(false)} disabled={saving}>
                  Cancel
                </Button>
                <Button type="button" onClick={handleSaveSimple} disabled={saving} data-testid="org-save-btn">
                  {saving ? "Saving..." : editingOrg ? "Save changes" : "Create"}
                </Button>
              </>
            ) : (
              <>
                {step > 1 ? (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setStep((s) => Math.max(1, s - 1))}
                    disabled={saving}
                    data-testid="org-step-back"
                  >
                    Back
                  </Button>
                ) : (
                  <Button type="button" variant="outline" onClick={() => handleDialogOpenChange(false)} disabled={saving}>
                    Cancel
                  </Button>
                )}
                {step === 1 ? (
                  <Button type="button" onClick={handleCompanyStep1Next} disabled={saving} data-testid="org-step-next">
                    Next
                  </Button>
                ) : null}
                {step === 2 ? (
                  <Button
                    type="button"
                    onClick={handleCompanyStep2Next}
                    disabled={saving}
                    data-testid="org-step-next"
                  >
                    {saving
                      ? "Saving..."
                      : createdCompany?.id
                        ? "Next"
                        : "Create & continue"}
                  </Button>
                ) : null}
                {step === 3 ? (
                  <Button
                    type="button"
                    onClick={handleCreateCompanyAdmin}
                    disabled={saving}
                    data-testid="org-create-admin-btn"
                  >
                    {saving ? "Creating..." : "Create admin"}
                  </Button>
                ) : null}
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={Boolean(deleteTarget)} onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {deleteTarget?.name || singularLabel}?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes the company. Remove its hospitals first, and reassign any users assigned to it.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={saving}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={(e) => {
                e.preventDefault();
                handleDelete();
              }}
              disabled={saving}
              data-testid="org-confirm-delete"
            >
              {saving ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Organizations;
